import type { FunctionComponent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Gap, GapSize } from '../../../shared/components/gap/gap';
import { TitledSection } from '../../../shared/components/titled-section/titled-section';
import { useDesktop } from '../../../shared/hooks/breakpoint';
import { createStyleHelper } from '../../../shared/utils/class-names';
import type { IUserProfileBonus } from '../../graphql/available-bonuses.graphql';
import { ActiveBonusCard } from '../active-bonus-card/active-bonus-card';
import { ActiveBonusHeading } from '../active-bonus-heading/active-bonus-heading';
import { AvailableBonusCard } from '../available-bonus-card/available-bonus-card';
import { AvailableBonusHeading } from '../available-bonus-heading/available-bonus-heading';
import { EmptyMessage } from '../empty-message/empty-message';
import { InactiveBonusCard } from '../inactive-bonus-card/inactive-bonus-card';
import { InactiveBonusHeading } from '../inactive-bonus-heading/inactive-bonus-heading';

import styles from './bonuses.module.scss';

const style = createStyleHelper(styles, 'bonuses');

export type IBonus = NonNullable<NonNullable<IUserProfileBonus[]>[0]>;

export interface IUserBonusesProps {
    availableBonuses: IBonus[];
    activeBonuses: IBonus[];
    inactiveBonuses: IBonus[];
    onClaimBonus: (id: string) => void;
    onForfeitBonus: (id: string) => void;
    isClaiming: boolean;
    isForfeiting: boolean;
}

export const UserBonuses: FunctionComponent<IUserBonusesProps> = ({
    availableBonuses,
    activeBonuses,
    inactiveBonuses,
    onClaimBonus,
    onForfeitBonus,
    isClaiming,
    isForfeiting,
}) => {
    const isFullDisplay = useDesktop();
    const intl = useIntl();

    return (
        <Gap size={GapSize.LARGE} className={style()}>
            {activeBonuses.length !== 0 ? (
                <TitledSection
                    title={intl.formatMessage({
                        id: 'account.bonuses.active-bonuses',
                        defaultMessage: 'Active Bonus',
                    })}
                >
                    <ActiveBonusHeading />
                    <Gap>
                        {activeBonuses.map((item) => {
                            const content = item.content;
                            const wageringPercent =
                                ((item.wageredAmount ?? 0) /
                                    (item.wageringRequirementAmount ?? 0)) *
                                100;
                            return (
                                <ActiveBonusCard
                                    key={item.id}
                                    id={item.id ?? ''}
                                    amount={item.grantedAmount ?? 0}
                                    currency={item.currencyCode ?? ''}
                                    title={content?.title ?? ''}
                                    wagered={wageringPercent}
                                    consumeTypeStrategy={item.consumeTypeStrategy}
                                    expiryDate={item.expiresOn}
                                    onForfeitBonus={onForfeitBonus}
                                    isForfeiting={isForfeiting}
                                />
                            );
                        })}
                    </Gap>
                </TitledSection>
            ) : null}

            <TitledSection
                title={intl.formatMessage({
                    id: 'account.bonuses.available-bonuses',
                    defaultMessage: 'Available Bonuses',
                })}
                description={
                    isFullDisplay
                        ? intl.formatMessage({
                              id: 'account.bonuses.available-description',
                              defaultMessage: 'You are only allowed to play one bonus at a time.',
                          })
                        : undefined
                }
            >
                <AvailableBonusHeading />
                <Gap>
                    {availableBonuses.length !== 0 ? (
                        availableBonuses.map((item) => {
                            const content = item.content;
                            return (
                                <AvailableBonusCard
                                    key={item.id}
                                    id={item.id ?? ''}
                                    image={content?.image}
                                    title={content?.title ?? ''}
                                    shortDescription={content?.subtitle}
                                    longDescription={content?.description}
                                    consumeTypeStrategy={item.consumeTypeStrategy}
                                    bonusExpiryDate={item.expiresOn}
                                    isClaimable={item.isClaimable ?? false}
                                    onClaim={onClaimBonus}
                                    isClaiming={isClaiming}
                                    bonus={item}
                                />
                            );
                        })
                    ) : (
                        <EmptyMessage
                            title={
                                <FormattedMessage
                                    id="account.bonuses.available-bonuses.empty.title"
                                    defaultMessage={'No available bonuses'}
                                />
                            }
                            message={
                                <FormattedMessage
                                    id="account.bonuses.available-bonuses.empty.message"
                                    defaultMessage={
                                        'You do not currently have any available bonuses. Check back later!'
                                    }
                                />
                            }
                        />
                    )}
                </Gap>
            </TitledSection>

            <TitledSection
                title={intl.formatMessage({
                    id: 'account.bonuses.inactive-bonuses',
                    defaultMessage: 'Past Bonuses',
                })}
                description={
                    isFullDisplay
                        ? intl.formatMessage({
                              id: 'account.bonuses.inactive-description',
                              defaultMessage: 'List of all previous bonuses',
                          })
                        : undefined
                }
            >
                <InactiveBonusHeading />
                <Gap>
                    {inactiveBonuses.length !== 0 ? (
                        inactiveBonuses.map((item) => {
                            const content = item.content;
                            return (
                                <InactiveBonusCard
                                    key={item.id}
                                    image={content?.image}
                                    title={content?.title ?? ''}
                                    shortDescription={content?.subtitle}
                                    longDescription={content?.description}
                                    amount={item.grantedAmount ?? 0}
                                    currency={item.currencyCode ?? ''}
                                    status={item.status ?? ''}
                                    consumeTypeStrategy={item.consumeTypeStrategy}
                                    bonus={item}
                                />
                            );
                        })
                    ) : (
                        <EmptyMessage
                            title={
                                <FormattedMessage
                                    id="account.bonuses.inactive-bonuses.empty.title"
                                    defaultMessage={'No inactive bonuses'}
                                />
                            }
                            message={
                                <FormattedMessage
                                    id="account.bonuses.inactive-bonuses.empty.message"
                                    defaultMessage={
                                        'You do not currently have any inactive bonuses.'
                                    }
                                />
                            }
                        />
                    )}
                </Gap>
            </TitledSection>

            <p className={style('footer')}>
                <FormattedMessage
                    id="account.bonuses.wagering-requirements-description"
                    defaultMessage="The wagering requirements is the sum of all wagers"
                />
            </p>
        </Gap>
    );
};
